import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Rocket, Code, Briefcase, ChartBar } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth, apiClient } from "../App";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState({
    skillLevel: "",
    goals: []
  });

  const skillLevels = [
    { id: "beginner", label: "Beginner", description: "New to SaaS, exploring ideas", icon: <Target className="w-6 h-6" /> },
    { id: "intermediate", label: "Intermediate", description: "Built 1-2 projects, some experience", icon: <Code className="w-6 h-6" /> },
    { id: "experienced", label: "Experienced", description: "Multiple projects, seeking growth", icon: <Briefcase className="w-6 h-6" /> },
    { id: "expert", label: "Expert", description: "Serial founder, scaling focus", icon: <Rocket className="w-6 h-6" /> }
  ];

  const goals = [
    { id: "side-project", label: "Build a side project" },
    { id: "quit-job", label: "Replace my full-time income" },
    { id: "learn", label: "Learn about SaaS businesses" },
    { id: "scale", label: "Scale an existing product" },
    { id: "validate", label: "Validate new ideas quickly" },
    { id: "network", label: "Connect with other founders" }
  ];

  const handleSkillSelect = (skillId) => {
    setSelections({ ...selections, skillLevel: skillId });
  };

  const handleGoalToggle = (goalId) => {
    const currentGoals = selections.goals;
    if (currentGoals.includes(goalId)) {
      setSelections({ ...selections, goals: currentGoals.filter(g => g !== goalId) });
    } else {
      setSelections({ ...selections, goals: [...currentGoals, goalId] });
    }
  };

  const handleSubmit = async () => {
    if (!selections.skillLevel || selections.goals.length === 0) {
      toast.error("Please complete all selections");
      return;
    }

    setLoading(true);
    try {
      await apiClient.put("/auth/onboarding", {
        skill_level: selections.skillLevel,
        goals: selections.goals
      });
      
      setUser({ ...user, skill_level: selections.skillLevel, goals: selections.goals });
      toast.success("Onboarding complete!");
      navigate("/dashboard/opportunities", { replace: true });
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-12">
          <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-[#7C3AED]' : 'bg-white/[0.08]'}`}></div>
          <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-[#7C3AED]' : 'bg-white/[0.08]'}`}></div>
        </div>

        {step === 1 ? (
          <>
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What's your experience level?
              </h1>
              <p className="text-zinc-400">This helps us personalize your opportunity feed</p>
            </div>

            <div className="grid gap-4">
              {skillLevels.map((skill) => (
                <button
                  key={skill.id}
                  className={`w-full flex items-center gap-4 p-6 rounded-2xl border transition-all text-left ${
                    selections.skillLevel === skill.id
                      ? 'bg-[#7C3AED]/10 border-[#7C3AED]/50'
                      : 'bg-[#111111] border-[#1F1F1F] hover:border-[#2A2A2A]'
                  }`}
                  onClick={() => handleSkillSelect(skill.id)}
                  data-testid={`skill-${skill.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    selections.skillLevel === skill.id
                      ? 'bg-[#7C3AED]/20 text-[#7C3AED]'
                      : 'bg-white/[0.03] text-zinc-400'
                  }`}>
                    {skill.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{skill.label}</h3>
                    <p className="text-sm text-zinc-500">{skill.description}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <Button
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-8 py-6"
                onClick={() => setStep(2)}
                disabled={!selections.skillLevel}
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What are your goals?
              </h1>
              <p className="text-zinc-400">Select all that apply</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {goals.map((goal) => (
                <button
                  key={goal.id}
                  className={`p-5 rounded-2xl border transition-all text-left ${
                    selections.goals.includes(goal.id)
                      ? 'bg-[#7C3AED]/10 border-[#7C3AED]/50'
                      : 'bg-[#111111] border-[#1F1F1F] hover:border-[#2A2A2A]'
                  }`}
                  onClick={() => handleGoalToggle(goal.id)}
                  data-testid={`goal-${goal.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selections.goals.includes(goal.id)
                        ? 'border-[#7C3AED] bg-[#7C3AED]'
                        : 'border-zinc-600'
                    }`}>
                      {selections.goals.includes(goal.id) && (
                        <svg className="w-3 h-3 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className="font-medium text-white">{goal.label}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                className="text-zinc-400 hover:text-white"
                onClick={() => setStep(1)}
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-full px-8 py-6"
                onClick={handleSubmit}
                disabled={loading || selections.goals.length === 0}
                data-testid="complete-onboarding-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    Start Exploring
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
